import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useRouter } from "next/router";
import { useForm } from "react-hook-form";
import { toast } from "react-toastify";
import deliveryCore from "../../core/delivery.core";
import shipCore from "../../core/ship.core";
import { useTranslation } from "../../core/translation.core";
import LoadingOvalCenterIcon from "../../icons/loadingOvalCenter.icon";
import useDeliveryVehichles from "../../hooks/useDeliveryVehiclesList.hook";
import { DeliveryLogisticItem, DeliverySchedule } from "../../types/delivery";
import { AddrType, PickupAddr, SfLocation, ShipAddr } from "../../types/shipSetting";
import { LabelAndInput, LabelAndSelect, LabelAndTextArea } from "../reuseables/form.comp";
import PencilIcon from "../../icons/pencil.icon";

type EditorForm = {
  addrType: AddrType;
  shipOptionId: string;
  recipientName: string;
  telCountryCode: string;
  tel: string;
  region: string;
  district: string;
  subDistrict: string;
  address: string;
  sfCode: string;
  sfAddress: string;
  pickupTel: string;
  pickupAddrEn: string;
};

export function LogisticShippingEditorForm({ logisticItem }: { logisticItem: DeliveryLogisticItem }) {
  const [showEditor, setShowEditor] = useState(false);
  const { t, msg } = useTranslation();
  const queryClient = useQueryClient();
  const { locale, isReady } = useRouter();
  const { data: shipSettings, isPending } = useQuery({
    queryKey: ["shipSettings", locale],
    queryFn: shipCore.getShipSettings,
    enabled: isReady,
  });

  const methods = useForm<EditorForm>({
    defaultValues: {
      addrType: logisticItem.addrType || "regular",
      shipOptionId: logisticItem.shipOptionId || "",
      recipientName: logisticItem.shipAddr?.recipientName || logisticItem.sfLocation?.recipientName || "",
      telCountryCode: logisticItem.shipAddr?.telCountryCode || logisticItem.sfLocation?.telCountryCode || "852",
      tel: String(logisticItem.shipAddr?.tel ?? logisticItem.sfLocation?.tel ?? ""),
      region: logisticItem.shipAddr?.region || "",
      district: logisticItem.shipAddr?.district || "",
      subDistrict: logisticItem.shipAddr?.subDistrict || "",
      address: logisticItem.shipAddr?.address || "",
      sfCode: logisticItem.sfLocation?.code || "",
      sfAddress: logisticItem.sfLocation?.address || "",
      pickupTel: logisticItem.pickupAddr?.tel || "",
      pickupAddrEn: logisticItem.pickupAddr?.addr?.en || "",
    },
  });

  const addrType = methods.watch("addrType");

  async function onSubmit(data: EditorForm) {
    try {
      const base = {
        trackingId: logisticItem.trackingId,
        addrType: data.addrType,
        shipOptionId: data.shipOptionId || undefined,
      };
      if (data.addrType === "regular") {
        const shipAddr: ShipAddr = {
          addrType: "regular",
          recipientName: data.recipientName,
          telCountryCode: data.telCountryCode,
          tel: Number(data.tel) || 0,
          countryCode: logisticItem.shipAddr?.countryCode || "852",
          countryName: logisticItem.shipAddr?.countryName || "",
          region: data.region,
          district: data.district,
          subDistrict: data.subDistrict,
          address: data.address,
          state: logisticItem.shipAddr?.state || "",
          city: logisticItem.shipAddr?.city || "",
          zipCode: logisticItem.shipAddr?.zipCode || "",
        };
        await deliveryCore.updateLogisticShipping({ ...base, shipAddr });
      } else if (data.addrType === "sfLocation") {
        const prev = logisticItem.sfLocation;
        const sfLocation: SfLocation = {
          addrType: "sfLocation",
          recipientName: data.recipientName,
          telCountryCode: data.telCountryCode,
          tel: data.tel,
          code: data.sfCode,
          address: data.sfAddress,
          servicePartner: prev?.servicePartner || "",
          shippingMethod: prev?.shippingMethod || ("" as SfLocation["shippingMethod"]),
          isHot: prev?.isHot || "",
          hoursMonFri: prev?.hoursMonFri || "",
          hoursSatSun: prev?.hoursSatSun || "",
        };
        await deliveryCore.updateLogisticShipping({ ...base, sfLocation });
      } else {
        const prev = logisticItem.pickupAddr;
        const pickupAddr: PickupAddr = {
          countryId: prev?.countryId || "",
          addr: {
            en: data.pickupAddrEn,
            zhHant: prev?.addr?.zhHant || data.pickupAddrEn,
            zhHans: prev?.addr?.zhHans || data.pickupAddrEn,
          },
          tel: data.pickupTel,
          openingHour: prev?.openingHour || { en: "", zhHant: "", zhHans: "" },
        };
        await deliveryCore.updateLogisticShipping({ ...base, pickupAddr });
      }
      queryClient.invalidateQueries({ queryKey: ["scheduledLogisticItems"] });
      queryClient.invalidateQueries({ queryKey: ["pendingLogisticItems"] });
      toast.success(t(msg.import.success));
      setShowEditor(false);
    } catch (e) {
      console.error(e);
      toast.error(t(msg.errors.contactAdmin));
    }
  }

  const shipOptions = shipSettings?.flatMap((s) => s.options).map((o) => ({ label: o.name, value: o.id })) || [];

  if (showEditor) {
    if (isPending) return <LoadingOvalCenterIcon />;

    return (
      <div className="mt-2 space-y-2">
        <form
          data-logistic-shipping-form
          onSubmit={methods.handleSubmit(onSubmit)}
          className="space-y-3 text-sm border border-gray-200 dark:border-gray-600 rounded-md p-3 dark:bg-her-blackOlive/30"
        >
          <p className="font-medium text-gray-800 dark:text-gray-200">{t(msg.delivery.shippingSnapshot)}</p>
          <div className="grid grid-cols-2 gap-2">
            <LabelAndSelect
              methods={methods}
              fieldName="addrType"
              label={t(msg.ship.shipping)}
              topLabelCss="block"
              setDefault={false}
              options={[
                { label: t(msg.delivery.addrTypeRegular), value: "regular" },
                { label: t(msg.delivery.addrTypeSf), value: "sfLocation" },
                { label: t(msg.delivery.addrTypePickup), value: "pickupLocation" },
              ]}
            />
            <LabelAndSelect
              methods={methods}
              fieldName="shipOptionId"
              label={t(msg.ship.shipOption)}
              topLabelCss="block"
              setDefault={false}
              options={[{ label: "—", value: "" }, ...shipOptions]}
            />
          </div>
          {addrType === "regular" && (
            <div className="grid grid-cols-6 gap-2">
              <LabelAndInput
                topLabelCss="col-span-2 mb-2"
                register={methods.register}
                fieldName="recipientName"
                label={t(msg.ship.name)}
              />
              <LabelAndInput
                topLabelCss="col-span-2 mb-2"
                register={methods.register}
                fieldName="telCountryCode"
                label={t(msg.ship.telCountryCode)}
              />
              <LabelAndInput
                topLabelCss="col-span-2 mb-2"
                register={methods.register}
                fieldName="tel"
                label={t(msg.pickupAddr.tel)}
              />
              <LabelAndInput
                topLabelCss="col-span-2 mb-2"
                register={methods.register}
                fieldName="region"
                label={t(msg.ship.region)}
              />
              <LabelAndInput
                topLabelCss="col-span-2 mb-2"
                register={methods.register}
                fieldName="district"
                label={t(msg.ship.district)}
              />
              <LabelAndInput
                topLabelCss="col-span-2 mb-2"
                register={methods.register}
                fieldName="subDistrict"
                label={t(msg.ship.subDistrict)}
              />
              <LabelAndTextArea
                topLabelCss="col-span-6 mb-2"
                register={methods.register}
                fieldName="address"
                label={t(msg.ship.addr)}
                rows={3}
              />
            </div>
          )}
          {addrType === "sfLocation" && (
            <>
              <LabelAndInput register={methods.register} fieldName="recipientName" label={t(msg.ship.name)} />
              <LabelAndInput register={methods.register} fieldName="telCountryCode" label={t(msg.ship.telCountryCode)} />
              <LabelAndInput register={methods.register} fieldName="tel" label={t(msg.pickupAddr.tel)} />
              <LabelAndInput register={methods.register} fieldName="sfCode" label={t(msg.ship.code)} />
              <LabelAndTextArea register={methods.register} fieldName="sfAddress" label={t(msg.ship.addr)} rows={3} />
            </>
          )}
          {addrType === "pickupLocation" && (
            <>
              <LabelAndTextArea register={methods.register} fieldName="pickupAddrEn" label={t(msg.ship.addr)} rows={3} />
              <LabelAndInput register={methods.register} fieldName="pickupTel" label={t(msg.pickupAddr.tel)} />
            </>
          )}
          <div className="grid grid-cols-2 gap-2">
            <button
              type="button"
              data-logistic-shipping-cancel
              onClick={() => setShowEditor(false)}
              className="w-full border border-gray-300 dark:border-gray-600 text-gray-800 dark:text-gray-200 py-2 px-3 rounded-lg text-sm hover:bg-gray-50 dark:hover:bg-her-blackOlive/50 transition-colors"
            >
              {t(msg.form.cancel)}
            </button>
            <button
              type="submit"
              data-logistic-shipping-submit
              className="w-full bg-her-blue dark:bg-her-blue text-white p-2 rounded-lg hover:opacity-90 transition-opacity"
            >
              {t(msg.form.update)}
            </button>
          </div>
        </form>
      </div>
    );
  }

  if (!logisticItem.addrType) {
    return (
      <div className="space-y-2 relative">
        <p className="text-xs text-gray-500 dark:text-gray-400">{t(msg.delivery.notAvailable)}</p>
        <button
          type="button"
          data-logistic-shipping-edit-toggle
          onClick={() => setShowEditor(true)}
          className="absolute top-0 right-0 py-2 px-3 rounded-lg text-sm text-her-robinEggBlue"
        >
          <PencilIcon className="w-4 h-4" />
        </button>
      </div>
    );
  }

  const shipName =
    logisticItem.shipOptionId && shipSettings
      ? shipSettings.flatMap((s) => s.options).find((o) => o.id === logisticItem.shipOptionId)?.name
      : undefined;

  return (
    <div className="space-y-2 relative">
      <div className="text-xs space-y-1 border border-gray-200 dark:border-gray-600 rounded-md p-3 dark:bg-her-darkSectionBg">
        <p className="font-medium">{t(msg.delivery.shippingSnapshot)}</p>
        {shipName && (
          <p>
            {t(msg.ship.shipOption)}: {shipName}
          </p>
        )}
        {logisticItem.addrType === "regular" && logisticItem.shipAddr && <RegularLines shipAddr={logisticItem.shipAddr} />}
        {logisticItem.addrType === "sfLocation" && logisticItem.sfLocation && <SfLines sf={logisticItem.sfLocation} />}
        {logisticItem.addrType === "pickupLocation" && logisticItem.pickupAddr && (
          <PickupLines pickup={logisticItem.pickupAddr} />
        )}
      </div>
      <button
        type="button"
        data-logistic-shipping-edit-toggle
        onClick={() => setShowEditor(true)}
        className="absolute top-0 right-0 py-2 px-3 rounded-lg text-sm text-her-robinEggBlue"
      >
        <PencilIcon className="w-4 h-4" />
      </button>
    </div>
  );
}

export function FinalizeScheduleForm({ schedule }: { schedule: DeliverySchedule }) {
  const { t, msg } = useTranslation();
  const queryClient = useQueryClient();
  const { data: vehicles } = useDeliveryVehichles();

  const methods = useForm<{ vehicleId: string; shipDate: string }>({
    defaultValues: { vehicleId: "", shipDate: "" },
  });

  async function onSubmit(data: { vehicleId: string; shipDate: string }) {
    try {
      await deliveryCore.finalizeSchedule({
        scheduleId: schedule.id,
        vehicleId: data.vehicleId,
        shipDate: data.shipDate,
      });
      queryClient.invalidateQueries({ queryKey: ["scheduledLogisticItems"] });
      queryClient.invalidateQueries({ queryKey: ["pendingLogisticItems"] });
      toast.success(t(msg.import.success));
    } catch (e) {
      console.error(e);
      toast.error(t(msg.errors.contactAdmin));
    }
  }

  return (
    <form
      data-role="finalize-schedule-form"
      onSubmit={methods.handleSubmit(onSubmit)}
      className="flex flex-col gap-2 border border-dashed border-her-blue/40 rounded-md p-3 dark:bg-her-darkSectionBg"
    >
      {/* <p className="text-xs font-medium">{t(msg.delivery.finalizeRun)}</p> */}
      <div className="grid grid-cols-[1fr_1fr_auto] gap-2">
        <LabelAndInput
          register={methods.register}
          fieldName="shipDate"
          label={t(msg.posMenuItems.deliveryDate)}
          type="date"
          required
        />
        <LabelAndSelect
          methods={methods}
          fieldName="vehicleId"
          label={t(msg.delivery.vehicleName)}
          topLabelCss="block"
          setDefault={true}
          options={vehicles?.map((v) => ({ label: v.name, value: v.id })) || []}
        />
        <button
          type="submit"
          data-role="finalize-schedule-submit"
          className="bg-her-greenSheen text-white px-2 py-2 h-9 rounded-md text-sm self-end my-2"
        >
          {t(msg.user.confirm)}
        </button>
      </div>
    </form>
  );
}

function RegularLines({ shipAddr }: { shipAddr: ShipAddr }) {
  return (
    <>
      <p>
        {shipAddr.recipientName} ({shipAddr.telCountryCode}) {shipAddr.tel}
      </p>
      <p>
        {shipAddr.region} {shipAddr.district} {shipAddr.subDistrict}
      </p>
      <p>{shipAddr.address}</p>
    </>
  );
}

function SfLines({ sf }: { sf: SfLocation }) {
  return (
    <>
      <p>
        {sf.recipientName} ({sf.telCountryCode}) {sf.tel}
      </p>
      <p>
        {sf.code} — {sf.address}
      </p>
    </>
  );
}

function PickupLines({ pickup }: { pickup: PickupAddr }) {
  const { locale } = useRouter();
  const loc = locale === "zhHant" ? "zhHant" : locale === "zhHans" ? "zhHans" : "en";
  const addr = pickup.addr?.[loc] || pickup.addr?.en || "";
  return (
    <>
      <p>{addr}</p>
      <p>{pickup.tel}</p>
    </>
  );
}
